import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { LoggerContext } from "context/LoggerContext";
import { OrganizationContext } from "context/OrganizationContext";
import styles from "./CheckoutSection.module.scss";
import SectionWrapper from "components/elements/SectionWrapper";
import { useCart } from "context/CartContext";
import Button from "components/elements/Button/Button";
import DynamicFontAwesomeIcon from "components/elements/DynamicFontAwesomeIcon";

// Import ausgelagerte Komponenten
import OrderItems from "./components/OrderItems";
import OrderSummary from "./components/OrderSummary";
import CustomerForm from "./components/CustomerForm";
import AddressForm from "./components/AddressForm";
import PaymentMethodSelector from "./components/PaymentMethodSelector";

// Import Hooks
import useTicketManager from "./hooks/useTicketManager";
import usePromoCodeManager from "./hooks/usePromoCodeManager";
import useCheckoutForm from "./hooks/useCheckoutForm";

// Import Services
import { removeItemFromCart, updateItemQuantity, submitCheckoutForm, finalizeOrder } from "./services/checkoutApiService";

// Import Hilfsfunktionen (jetzt aus dem modularen helpers-Verzeichnis)
import { 
	formatCurrency,
	processApiItems,
	groupTaxesByRate,
	calculateOrderTotals
} from "./helpers";

/**
 * CheckoutSection component - Handles the checkout process including form, validation, and submission
 * 
 * @param {Object} props - Component props
 * @param {Object} props.data - Checkout data from API
 * @param {function} props.onOrderUpdate - Callback when order is updated
 * @param {string} props.title - Custom section title
 */
const CheckoutSection = (props) => {
	const { logDebug, logError } = useContext(LoggerContext);
	const organization = useContext(OrganizationContext);
	const { cart, clearCart } = useCart();
	const navigate = useNavigate();

	// === Local State ===
	const [currentCheckoutData, setCurrentCheckoutData] = useState(props.data || {});
	const [/*apiError, */setApiError] = useState(null);
	const [/*submitSuccess, */setSubmitSuccess] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Initialize form with default values
	const initialFormData = {
		email: "",
		name: props.data?.customer_name || "",
		lastName: "",
		formOfAddress: "Herr",
		title: "",
		paymentMethod: "invoice",
		country: "DE",
		street: "",
		addressSuffix: "",
		zipCode: "",
		city: "",
		isCompany: false,
		companyName: "",
		companyPosition: "",
		companyPositionId: "",
		companyIndustry: "",
		companyIndustryId: "",
		companySize: "",
		companySizeId: "",
		phone: "",
		poNumber: "",
		vatCountry: "DE",
		vatId: "",
		promoCode: "",
		acceptTerms: false
	};

	// Use custom hooks
	const { formData, setFormData, formErrors, setFormErrors, handleInputChange, clearError, validateForm, formatOrderDataForApi } = 
		useCheckoutForm(initialFormData);
		
	const { 
		expandedTickets, 
		setExpandedTickets, 
		generateTicketsForEventArticles, 
		handleTicketChange, 
		syncTicketsWithArticles, 
		updateTicketsWithPersonalInfo 
	} = useTicketManager(formData);
	
	const {
		activeCoupon,
		setActiveCoupon,
		promoApplied,
		setPromoApplied,
		couponError,
		setCouponError,
		isApplyingPromo,
		showPromoField,
		setShowPromoField,
		togglePromoField,
		applyPromoCode,
		removePromoCode
	} = usePromoCodeManager({ 
		cartToken: props.data?.cart_token, 
		onOrderUpdate: props.onOrderUpdate,
		organization
	});

	// === useEffect: Update checkout data and form data when props.data changes ===
	useEffect(() => {
		if (props.data) {
			setCurrentCheckoutData(props.data);

			// If a coupon exists, apply it:
			if (props.data.coupon) {
				setActiveCoupon({
					code: props.data.coupon.code,
					value: Number(props.data.coupon.value || 0),
					typeId: Number(props.data.coupon.type_id || 0),
					reduction: Number(props.data.coupon.reduction || 0)
				});
				setPromoApplied(true);
				setShowPromoField(false);
				setFormData(prev => ({ ...prev, promoCode: props.data.coupon.code }));
			}

			// If address data exists, fill form data:
			if (props.data.invoice_address) {
				const address = props.data.invoice_address;
				const buyer = props.data.buyer || {};
				const countryMapping = { "Deutschland": "DE", "Österreich": "AT", "Schweiz": "CH" };
				let vatCountry = "DE", vatId = "";
				if (buyer.vat_number) {
					if (buyer.vat_number.startsWith("DE")) { vatCountry = "DE"; vatId = buyer.vat_number.substring(2); }
					else if (buyer.vat_number.startsWith("AT")) { vatCountry = "AT"; vatId = buyer.vat_number.substring(2); }
					else if (buyer.vat_number.startsWith("CH")) { vatCountry = "CH"; vatId = buyer.vat_number.substring(3); }
					else { vatId = buyer.vat_number; }
				}
				setFormData(prev => ({
					...prev,
					name: buyer.name || props.data.customer_name || "",
					lastName: buyer.last_name || "",
					formOfAddress: buyer.form_of_address || "Herr",
					title: buyer.title || "",
					email: buyer.email || address.invoiceEmailAddress || "",
					phone: buyer.phone || "",
					street: address.invoicePostalAddress?.streetAddress || "",
					addressSuffix: address.invoicePostalAddress?.addressSuffix || "",
					zipCode: address.invoicePostalAddress?.postalCode || "",
					city: address.invoicePostalAddress?.city || "",
					country: countryMapping[address.invoicePostalAddress?.country] || "DE",
					companyName: buyer.company_name || "",
					isCompany: !!buyer.company_name,
					companyPosition: buyer.company_position || "",
					companyPositionId: buyer.company_position_id || "",
					companyIndustry: buyer.company_industry || "",
					companyIndustryId: buyer.company_industry_id || "",
					companySize: buyer.company_size || "",
					companySizeId: buyer.company_size_id || "",
					poNumber: buyer.po_number || "",
					vatCountry: vatCountry,
					vatId: vatId
				}));
			}

			// Generate tickets based on articles and existing invitations (if any)
			if (props.data.item?.article) {
				// Add debug logging to see the content of article array
				logDebug("Available articles in props.data:", {
					articleCount: props.data.item.article.length,
					articles: props.data.item.article.map(a => ({
						id: a.id,
						title: a.instance?.title,
						kind_id: a.instance?.kind_id,
						hasEventData: !!a.instance?.event,
						eventData: a.instance?.event
					}))
				});

				const tickets = generateTicketsForEventArticles(
					props.data.item.article,
					props.data.invitations || []
				);

				// Check if tickets were correctly generated
				logDebug("Initial tickets generation:", {
					count: tickets.length,
					tickets: tickets.map(t => ({
						id: t.id,
						agendaCount: t.availableAgendas?.length || 0,
						requiredAmount: t.requiredAgendaAmount
					}))
				});

				if (tickets.length > 0) {
					setExpandedTickets(tickets);
				}
			}
		}
	}, [props.data, generateTicketsForEventArticles, setExpandedTickets, setFormData, setActiveCoupon, 
		setPromoApplied, setShowPromoField, logDebug]);

	// Process API items and cart items
	const apiItems = useMemo(() => processApiItems(currentCheckoutData), [currentCheckoutData]);
	const cartItems = useMemo(() => cart?.items || [], [cart]);
	const items = useMemo(() => (apiItems.length ? apiItems : cartItems), [apiItems, cartItems]);

	// Make sure we have tickets for all event articles
	useEffect(() => {
		// If we don't have any items yet, we can't create tickets
		if (!items.length) return;

		// Check if we already have tickets
		if (expandedTickets.length > 0) {
			// Verify that ticket counts match item quantities
			let needsUpdate = false;

			// Check that all event items have corresponding tickets
			items.forEach(item => {
				// Skip non-event items (using the correct path)
				if (!item.originalArticle?.instance?.kind_id === 3) return;

				// Count tickets for this item
				const itemTickets = expandedTickets.filter(t => t.baseItemId === item.id);

				// If ticket count doesn't match item quantity, we need to update
				if (itemTickets.length !== item.quantity) {
					needsUpdate = true;
				}
			});

			// If all items have correct ticket counts, we're done
			if (!needsUpdate) return;
		}

		// If we get here, we need to generate/update tickets
		// For API items, use currentCheckoutData's article array
		if (apiItems.length > 0 && currentCheckoutData?.item?.article) {
			const newTickets = generateTicketsForEventArticles(
				currentCheckoutData.item.article,
				currentCheckoutData.invitations || []
			);

			if (newTickets.length > 0) {
				setExpandedTickets(newTickets);
			}
		}
		// For cart items, create placeholder objects (fixed to use correct path)
		else if (cartItems.length > 0) {
			const newTickets = cartItems
				.filter(item => item.originalArticle?.instance?.event?.linked_agenda_Amount > 0)
				.flatMap(item => {
					const tickets = [];
					// Get event info from the correct path
					const eventInfo = item.originalArticle?.instance?.event || {};

					for (let i = 0; i < item.quantity; i++) {
						tickets.push({
							id: `${item.id}_${i}_cart`,
							baseItemId: item.id,
							ticketNumber: i + 1,
							isForOrderer: i === 0,
							invitee: {
								email: i === 0 ? formData.email : "",
								firstName: i === 0 ? formData.name : "",
								lastName: i === 0 ? formData.lastName : ""
							},
							preferences: [],
							availableAgendas: eventInfo.selectable_agendas || [],
							requiredAgendaAmount: eventInfo.linked_agenda_Amount || 0
						});
					}
					return tickets;
				});

			if (newTickets.length > 0) {
				setExpandedTickets(newTickets);
			}
		}
	}, [items, expandedTickets, expandedTickets.length, currentCheckoutData, apiItems.length, cartItems, 
		generateTicketsForEventArticles, formData, setExpandedTickets]);

	// Calculate order totals using our helper
	const { taxGroups, ...orderTotals } = useMemo(() => {
		if (currentCheckoutData?.total_price) {
			// If API provides totals, use those but recalculate taxes for display
			const subtotal = Number(currentCheckoutData.total_price.original_total_gross_amount || 0);
			const discount = Number(currentCheckoutData.total_price.total_coupon_discount || 0);
			const discountedSubtotal = subtotal - discount;
			const total = Number(currentCheckoutData.total_price.total_gross_amount || 0);

			// Always recalculate tax groups for display
			const taxGroups = groupTaxesByRate(items);
			const tax = Object.values(taxGroups).reduce((sum, val) => sum + val, 0);

			return {
				subtotal,
				discount,
				discountedSubtotal,
				total,
				tax,
				taxGroups
			};
		} else {
			// Calculate totals from scratch
			return calculateOrderTotals(items, activeCoupon);
		}
	}, [items, currentCheckoutData, activeCoupon]);

	// === Calculate checkout data (price summary) ===
	const checkoutData = useMemo(() => {
		// Currency from API or default to Euro
		const currency = currentCheckoutData?.total_price?.currency === "Euro" ? "€" : currentCheckoutData?.total_price?.currency || "€";

		return {
			title: props.title || currentCheckoutData?.organization_name || "Bestellung",
			subtotal: orderTotals.subtotal,
			discount: orderTotals.discount,
			discountedSubtotal: orderTotals.discountedSubtotal,
			tax: orderTotals.tax,
			total: orderTotals.total,
			currency,
			items,
			taxesByRate: taxGroups
		};
	}, [props.title, currentCheckoutData, items, orderTotals, taxGroups]);

	// Extended input change handler that also updates tickets
	const handleFormInputChange = (e) => {
		const { name, value } = handleInputChange(e);
		
		// Reset coupon error if editing promo code
		if (name === "promoCode" && couponError) setCouponError("");
		
		// Update tickets with personal information
		updateTicketsWithPersonalInfo(name, value);
	};

	/**
	 * Removes an item from the cart/checkout
	 * @param {string|number} itemId - ID of the item to remove
	 */
	const handleRemoveItem = async (itemId) => {
		if (!props.data.cart_token) {
			logDebug("No cartToken available for item removal", { propsCartToken: props.data.cart_token });
			return;
		}

		setIsSubmitting(true);

		try {
			const currentArticles = (currentCheckoutData.item?.article || [])
				.filter(article => article.id !== itemId)
				.map(article => ({
					id: article.id,
					count: String(article.count || 1) // Convert count to string
				}));

			const filteredTickets = expandedTickets.filter(ticket => ticket.baseItemId !== itemId);

			const response = await removeItemFromCart({
				cartToken: props.data.cart_token,
				currentArticles,
				tickets: filteredTickets,
				formData,
				otk: organization?.otk
			});

			if (response.httpStatus === 200 && response.data) {
				setCurrentCheckoutData(response.data);
				setExpandedTickets(filteredTickets);
				
				// Clear any validation errors for removed tickets
				setFormErrors(prev => {
					const newErrors = { ...prev };
					Object.keys(newErrors).forEach(key => {
						if (key.includes(`_${itemId}_`)) {
							delete newErrors[key];
						}
					});
					return newErrors;
				});
			} else {
				logError("Item removal failed", response);
				setApiError("Artikel konnte nicht entfernt werden");
			}
		} catch (error) {
			logError("Error removing item:", error);
			setApiError("Fehler beim Entfernen des Artikels");
		} finally {
			setIsSubmitting(false);
		}
	};

	/**
	 * Updates the quantity of an item in the cart/checkout
	 * @param {string|number} itemId - ID of the item to update
	 * @param {number} newQuantity - New quantity for the item
	 */
	const handleQuantityChange = async (itemId, newQuantity) => {
		if (!props.data.cart_token) {
			logDebug("No cartToken available for quantity change", { propsCartToken: props.data.cart_token });
			return;
		}

		if (newQuantity < 1) {
			logDebug("Quantity cannot be less than 1");
			return;
		}

		setIsSubmitting(true);

		try {
			// Update the Article array in currentCheckoutData
			setCurrentCheckoutData(prev => {
				if (prev?.item?.article) {
					return {
						...prev,
						item: {
							...prev.item,
							article: prev.item.article.map(article =>
								article.id === itemId
									? { ...article, count: newQuantity }
									: article
							)
						}
					};
				}
				return prev;
			});

			const currentArticles = (currentCheckoutData.item?.article || []).map(article => ({
				id: article.id,
				count: String(article.id === itemId ? newQuantity : article.count || 1) // Convert count to string
			}));

			// This will update tickets based on article quantities
			syncTicketsWithArticles(currentArticles, currentCheckoutData);

			const response = await updateItemQuantity({
				cartToken: props.data.cart_token,
				currentArticles,
				tickets: expandedTickets,
				formData,
				otk: organization?.otk
			});

			if (response.httpStatus === 200 && response.data) {
				// Set API response (overrides local update)
				setCurrentCheckoutData(response.data);
			} else {
				logError("Quantity change failed", response);
				setApiError("Änderung der Menge fehlgeschlagen");
			}
		} catch (error) {
			logError("Error updating quantity:", error);
			setApiError("Fehler beim Aktualisieren der Menge");
		} finally {
			setIsSubmitting(false);
		}
	};

	/**
	 * Handles applying a promo code from the UI
	 */
	const handleApplyPromoCode = async () => {
		const result = await applyPromoCode(formData.promoCode);
		if (result.success && result.data) {
			setCurrentCheckoutData(prev => ({
				...prev,
				total_price: result.data.total_price,
				coupon: result.data.coupon
			}));
		}
	};

	/**
	 * Handles removing a promo code from the UI
	 */
	const handleRemovePromoCode = async () => {
		const result = await removePromoCode();
		if (result.success && result.data) {
			setCurrentCheckoutData(prev => ({
				...prev,
				total_price: result.data.total_price,
				coupon: null
			}));
			setFormData(prev => ({ ...prev, promoCode: "" }));
		}
	};

	/**
	 * Handles form submission
	 * @param {Event} e - Form submit event
	 */
	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!validateForm(expandedTickets)) {
			return;
		}
		
		setIsSubmitting(true);
		setApiError(null);
		
		try {
			if (props.data.cart_token) {
				const payload = formatOrderDataForApi(activeCoupon, expandedTickets, apiItems, items);
				
				// Step 1: Submit checkout form to get order ID
				const checkoutResponse = await submitCheckoutForm({
					cartToken: props.data.cart_token,
					payload,
					otk: organization?.otk
				});
				
				// Log checkout response
				logDebug("Checkout response received:", checkoutResponse);
				
					// Check if checkout was successful, if not stop execution
				if (!checkoutResponse?.success || !checkoutResponse?.data?.id) {
					const errorMessage = checkoutResponse?.message || "Fehler bei der Bestellung. Bitte versuchen Sie es später erneut.";
					setApiError(errorMessage);
					// Early return to stop execution
					return;
				}
				
				// Step 2: If checkout successful, finalize the order to generate tickets
				const orderId = checkoutResponse.data.id;
				
				// Finalize order with ticket generation
				const finalizeResponse = await finalizeOrder({
					orderId,
					orderData: payload, // Send the same payload to the finalization endpoint
					otk: organization?.otk
				});
				
				logDebug("Order finalization response:", finalizeResponse);
				
				// Check if finalization was successful, if not stop execution
				if (!finalizeResponse?.success) {
					const errorMessage = finalizeResponse?.message || "Die Bestellung wurde aufgenommen, aber es gab ein Problem bei der Generierung der Tickets.";
					setApiError(errorMessage);
					// Early return to stop execution
					return;
				}
				
				setSubmitSuccess(true);
				if (!apiItems.length) clearCart();
				// Navigation to success page
				setTimeout(() => navigate("/checkout/success"), 2000);
			} else {
				// Mock flow if no cartToken is available:
				if (!apiItems.length) clearCart();
				setSubmitSuccess(true);
				setTimeout(() => navigate("/checkout/success"), 2000);
			}
		} catch (error) {
			logError("Error during payment processing:", error);
			setApiError(error.message || "Fehler bei der Verarbeitung der Bestellung.");
		} finally {
			setIsSubmitting(false);
		}
	};

	// === Render ===
	return (
		<SectionWrapper
			className={styles.CheckoutSection}
			padding_y={5}
			col_width={12}
			{...props}>
			<div className="row g-4">
				{/* Order Summary */}
				<div className="col-12 col-lg-6">
					<div className={`p-0 py-3 py-md-6 px-md-3 card shadow-sm border-0 bg-secondary text-secondary-contrast`}>
						<div className="card-body">
							<h2 className="fs-5 mb-4">{checkoutData.title}</h2>
							<div className="display-5 fw-bold mb-4">
								{formatCurrency(checkoutData.total, checkoutData.currency)}
							</div>

							{/* Product Details with OrderItems Component */}
							<OrderItems
								items={checkoutData.items}
								currency={checkoutData.currency}
								isSubmitting={isSubmitting}
								expandedTickets={expandedTickets}
								formData={formData}
								formErrors={formErrors}
								onTicketChange={handleTicketChange}
								onClearError={clearError}
								onQuantityChange={handleQuantityChange}
								onRemoveItem={handleRemoveItem}
							/>

							{/* Price Summary with OrderSummary Component */}
							<OrderSummary
								subtotal={checkoutData.subtotal}
								discount={checkoutData.discount}
								discountedSubtotal={checkoutData.discountedSubtotal}
								total={checkoutData.total}
								tax={checkoutData.tax}
								taxesByRate={checkoutData.taxesByRate}
								currency={checkoutData.currency}
								showPromoField={showPromoField}
								promoApplied={promoApplied}
								couponError={couponError}
								isApplyingPromo={isApplyingPromo}
								formData={formData}
								activeCoupon={activeCoupon}
								onInputChange={handleFormInputChange}
								onTogglePromoField={togglePromoField}
								onApplyPromoCode={handleApplyPromoCode}
								onRemovePromoCode={handleRemovePromoCode}
							/>
						</div>
					</div>
				</div>

				{/* Payment Form */}
				<div className="col-12 col-lg-6">
					<div className="p-0 p-3 py-md-6 px-md-2">
						<h2 className="fs-4 mb-4">Zahlungsinformationen</h2>
						{/* {apiError && (
							<div 
								className="alert alert-danger mb-4" 
								role="alert"
								aria-live="assertive">
								<DynamicFontAwesomeIcon 
									iconName="exclamationCircle" 
									className="me-2" 
									aria-hidden="true" 
								/>
								{apiError}
							</div>
						)}
						{submitSuccess && (
							<div 
								className="alert alert-success mb-4" 
								role="alert"
								aria-live="assertive">
								<DynamicFontAwesomeIcon 
									iconName="checkCircle" 
									className="me-2" 
									aria-hidden="true" 
								/>
								Bestellung erfolgreich aufgegeben. Sie werden weitergeleitet...
							</div>
						)} */}
						<form onSubmit={handleSubmit} noValidate>
							{/* Customer Information Component */}
							<CustomerForm 
								formData={formData}
								formErrors={formErrors}
								onChange={handleFormInputChange}
							/>

							<h3 className="fs-5 mb-3">Zahlungsmethode</h3>
							{/* Payment Method Selector Component */}
							<div className="mb-4">
								<PaymentMethodSelector 
									selectedMethod={formData.paymentMethod}
									onChange={handleFormInputChange}
								/>
							</div>

							{/* Address Form Component */}
							<AddressForm 
								formData={formData}
								formErrors={formErrors}
								onChange={handleFormInputChange}
							/>
							
							<div className="mb-4 form-check">
								<input
									type="checkbox"
									className={`form-check-input ${formErrors.acceptTerms ? "is-invalid" : ""}`}
									id="acceptTerms"
									name="acceptTerms"
									checked={formData.acceptTerms}
									onChange={handleFormInputChange}
									required
									aria-required="true"
									data-field="acceptTerms"
								/>
								<label className="form-check-label small" htmlFor="acceptTerms">
									Ich akzeptiere die <a href="/datenschutz" target="_blank" rel="noopener noreferrer">Datenschutzerklärung</a>.
								</label>
								{formErrors.acceptTerms && (
									<div 
										className="invalid-feedback d-block"
										role="alert"
										aria-live="polite">
										{formErrors.acceptTerms}
									</div>
								)}
							</div>
							<Button
								type="submit"
								variant="primary"
								size="lg"
								className="w-100 mt-4"
								disabled={isSubmitting || checkoutData.items.length === 0}
								aria-label="Zahlungsvorgang abschließen">
								<DynamicFontAwesomeIcon 
									iconName="lock" 
									className="me-2" 
									aria-hidden="true" 
								/>
								{isSubmitting ? "Bitte warten..." : `Jetzt bezahlen (${formatCurrency(checkoutData.total, checkoutData.currency)})`}
							</Button>
						</form>
					</div>
				</div>
			</div>
		</SectionWrapper>
	);
};

export default CheckoutSection;
